import type { Component } from 'solid-js'
import { Show } from 'solid-js'
import { formatBytes, transferProgress, transferRate, type TransferProgress as TP } from '../ipfs/transfer-progress'

// ---------------------------------------------------------------------------
// Geeky-but-tidy car-mirror / gateway transfer readout.
//
// Minimal by default (one line); the geeky per-round stats live behind a
// <details> toggle. `compact` renders just the summary line (for the title-bar
// sync popover). Reads live stats from the transfer-progress store by id.
//
// Honesty note: car-mirror discovers the DAG as it walks, so there's no reliable
// "% complete" — the bar is intentionally indeterminate while transferring and
// only fills on completion.
// ---------------------------------------------------------------------------

function verb(p: TP): string {
	const pull = p.kind === 'pull'
	switch (p.phase) {
		case 'resolving':
			return pull ? 'Resolving…' : 'Preparing…'
		case 'transferring':
			return pull ? 'Pulling' : 'Pushing'
		case 'decoding':
			return 'Decoding…'
		case 'done':
			return pull ? 'Pulled' : 'Pushed'
		case 'error':
			return pull ? 'Pull failed' : 'Push failed'
		default:
			return ''
	}
}

const STYLE = `
	.cm-xfer { --cm-accent: var(--wa-color-brand-fill-loud, #3b82f6); }
	.cm-xfer details > summary { list-style: none; cursor: pointer; }
	.cm-xfer details > summary::-webkit-details-marker { display: none; }
	.cm-xfer details > summary::marker { content: ''; }
	.cm-xfer .cm-caret { transition: transform .15s ease; opacity: .5; }
	.cm-xfer details[open] .cm-caret { transform: rotate(90deg); }
	.cm-xfer .cm-glyph.spin { animation: cm-pulse 1.1s ease-in-out infinite; }
	@keyframes cm-pulse { 0%,100% { opacity: .45 } 50% { opacity: 1 } }
	.cm-bar { position: relative; height: 4px; border-radius: 3px; overflow: hidden;
		background: color-mix(in srgb, currentColor 15%, transparent); }
	.cm-bar > i { position: absolute; inset: 0; display: block; border-radius: 3px; }
	.cm-bar[data-phase='transferring'] > i,
	.cm-bar[data-phase='resolving'] > i,
	.cm-bar[data-phase='decoding'] > i {
		width: 40%; background: var(--cm-accent);
		animation: cm-indeterminate 1.15s ease-in-out infinite;
	}
	.cm-bar[data-phase='done'] > i { inset: 0; background: var(--wa-color-success-fill-loud, #16a34a); }
	.cm-bar[data-phase='error'] > i { inset: 0; background: var(--wa-color-danger-fill-loud, #dc2626); }
	@keyframes cm-indeterminate { 0% { left: -40% } 100% { left: 100% } }
`

/** Live summary line: "⇊ Pulling  142 blocks · 384 KB   ~128 KB/s" */
const Summary: Component<{ p: TP }> = (props) => {
	const p = () => props.p
	const rate = () => transferRate(p())
	return (
		<span flex='~ items-center gap-2' font-mono text-xs>
			<span
				class={`cm-glyph${p().phase === 'transferring' || p().phase === 'resolving' || p().phase === 'decoding' ? ' spin' : ''}`}
				classList={{ 'text-green': p().phase === 'done', 'text-red': p().phase === 'error' }}
			>
				{p().kind === 'pull' ? '⇊' : '⇈'}
			</span>
			<span>{verb(p())}</span>
			<Show when={p().phase !== 'resolving' && (p().blocks > 0 || p().bytes > 0)}>
				<span opacity-80>
					{p().blocks}
					{' blocks · '}
					{formatBytes(p().bytes)}
				</span>
			</Show>
			<Show when={p().source === 'gateway'}>
				<span text='[10px]' opacity-50 uppercase tracking-wide>via gw</span>
			</Show>
			<Show when={p().phase === 'transferring' && p().bytes > 0}>
				<span opacity-50>~{formatBytes(rate())}/s</span>
			</Show>
		</span>
	)
}

/** The geeky per-round stat line(s) shown when expanded. */
const Details: Component<{ p: TP }> = (props) => {
	const p = () => props.p
	return (
		<div font-mono text='[11px]' opacity-70 mt-1 flex='~ col gap-0.5'>
			<Show
				when={p().source === 'car-mirror'}
				fallback={
					<div>
						via gateway
						<Show when={p().snapshots != null}>{` · ${p().snapshots} snapshot(s)`}</Show>
						<Show when={p().requests != null}>{` · ${p().requests} req`}</Show>
					</div>
				}
			>
				<div>
					<Show when={p().round != null}>
						round {p().round}
						<Show when={p().maxRounds != null}>/{p().maxRounds}</Show>
						{' · '}
					</Show>
					<Show when={p().frontier != null}>frontier {p().frontier}{' · '}</Show>
					<Show when={p().bloomBytes != null && p().bloomBytes! > 0}>
						bloom {formatBytes(p().bloomBytes)}
						<Show when={p().bloomK}>{` (k=${p().bloomK})`}</Show>
						{' · '}
					</Show>
					<Show when={p().requests != null}>{p().requests} req</Show>
				</div>
			</Show>
			<Show when={(p().blocksThisRound ?? 0) > 0 || (p().bytesThisRound ?? 0) > 0}>
				<div opacity-80>
					this round: {p().blocksThisRound ?? 0} blocks · {formatBytes(p().bytesThisRound)}
				</div>
			</Show>
			<Show when={p().kind === 'push' && p().step}>
				<div opacity-80>step: {p().step}</div>
			</Show>
			<Show when={p().error}>
				<div text-red break-words>{p().error}</div>
			</Show>
		</div>
	)
}

export const TransferProgress: Component<{ id?: string; compact?: boolean }> = (props) => {
	const prog = () => (props.id ? (transferProgress[props.id] as TP | undefined) : undefined)
	return (
		<Show when={prog()}>
			{(p) => (
				<div class='cm-xfer' w-full>
					<style>{STYLE}</style>
					<Show
						when={!props.compact}
						fallback={<Summary p={p()} />}
					>
						<details>
							<summary flex='~ items-center justify-between gap-2'>
								<span flex-grow-1 min-w-0>
									<Summary p={p()} />
								</span>
								<span class='cm-caret i-ph:caret-right-bold' text-xs flex-shrink-0 />
							</summary>
							<Details p={p()} />
						</details>
						<div class='cm-bar' data-phase={p().phase} mt-1 text='[var(--cm-accent)]'>
							<i />
						</div>
					</Show>
				</div>
			)}
		</Show>
	)
}
